import { useEffect, useState } from "react";

import { BUILD_STAMP } from "../buildStamp";
import type { RouteDebugInfo } from "../RouteApp";

declare const __BUILD_SHA__: string | undefined;

function resolveBuildSha(): string {
  if (typeof __BUILD_SHA__ === "string" && __BUILD_SHA__.trim().length > 0) {
    return __BUILD_SHA__;
  }
  return BUILD_STAMP;
}

type AuthDebugState = {
  sessionPresent: boolean;
  accessTokenPresent: boolean;
  issuerHost: string;
  apiCoreAuthHeaderAttached: boolean | null;
};

function decodeBase64Url(input: string): string | null {
  try {
    const normalized = input.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    return typeof atob === "function" ? atob(padded) : null;
  } catch {
    return null;
  }
}

function parseIssuerHost(accessToken: string | null): string {
  if (!accessToken) return "(none)";

  const parts = accessToken.split(".");
  if (parts.length < 2) return "(invalid)";

  const payloadRaw = decodeBase64Url(parts[1]);
  if (!payloadRaw) return "(invalid)";

  try {
    const payload = JSON.parse(payloadRaw) as { iss?: unknown };
    const issuer = typeof payload.iss === "string" ? payload.iss : "";
    if (!issuer) return "(missing)";
    return new URL(issuer).hostname || "(missing)";
  } catch {
    return "(invalid)";
  }
}

export default function RouteStampFooter(props: { routeInfo: RouteDebugInfo }): JSX.Element {
  const buildSha = resolveBuildSha();
  const [authDebug, setAuthDebug] = useState<AuthDebugState>({
    sessionPresent: false,
    accessTokenPresent: false,
    issuerHost: "(none)",
    apiCoreAuthHeaderAttached: null,
  });

  useEffect(() => {
    let mounted = true;

    function setApiCoreHeaderState(attached: boolean | null): void {
      if (!mounted) return;
      setAuthDebug((prev) => ({ ...prev, apiCoreAuthHeaderAttached: attached }));
    }

    function readInitialApiCoreState(): void {
      try {
        const attached = (globalThis as any).__VERITYEDGE_API_CORE_AUTH_HEADER_ATTACHED;
        if (typeof attached === "boolean") {
          setApiCoreHeaderState(attached);
        }
      } catch {
        // ignore
      }
    }

    function handleApiCoreHeaderEvent(event: Event): void {
      const customEvent = event as CustomEvent<{ attached?: unknown }>;
      const attached = customEvent?.detail?.attached;
      if (typeof attached === "boolean") {
        setApiCoreHeaderState(attached);
      }
    }

    readInitialApiCoreState();
    if (typeof window !== "undefined") {
      window.addEventListener("verityedge:api-core-auth-header", handleApiCoreHeaderEvent as EventListener);
    }

    async function loadAuthDebug(): Promise<void> {
      try {
        const { getSupabaseClient } = await import("../lib/supabase");
        const supabase = getSupabaseClient();
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;

        const session = data?.session ?? null;
        const accessToken = session?.access_token ?? null;

        setAuthDebug({
          sessionPresent: Boolean(session),
          accessTokenPresent: Boolean(accessToken),
          issuerHost: parseIssuerHost(accessToken),
          apiCoreAuthHeaderAttached:
            typeof (globalThis as any).__VERITYEDGE_API_CORE_AUTH_HEADER_ATTACHED === "boolean"
              ? Boolean((globalThis as any).__VERITYEDGE_API_CORE_AUTH_HEADER_ATTACHED)
              : null,
        });
      } catch {
        if (!mounted) return;
        setAuthDebug((prev) => ({
          ...prev,
          sessionPresent: false,
          accessTokenPresent: false,
          issuerHost: "(none)",
        }));
      }
    }

    void loadAuthDebug();

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("verityedge:api-core-auth-header", handleApiCoreHeaderEvent as EventListener);
      }
      mounted = false;
    };
  }, []);

  return (
    <footer style={{ margin: "1.25rem auto 0", maxWidth: "var(--container)", padding: "0 var(--gutter) 1rem", fontSize: "0.85rem", color: "var(--muted)" }}>
      <div>BUILD_SHA: {buildSha}</div>
      <div>
        Path: {props.routeInfo.path || "(unknown)"} · Route: {props.routeInfo.route}
      </div>
      <div>
        Auth Debug: sessionPresent: {authDebug.sessionPresent ? "yes" : "no"} · accessTokenPresent: {authDebug.accessTokenPresent ? "yes" : "no"} · tokenIssuerHost: {authDebug.issuerHost} · apiCoreAuthHeaderAttached: {authDebug.apiCoreAuthHeaderAttached === null ? "unknown" : authDebug.apiCoreAuthHeaderAttached ? "yes" : "no"}
      </div>
    </footer>
  );
}
